import React, { FC, PropsWithChildren } from 'react';

import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

import { createShouldForwardProp } from 'helpers/shouldForwardProp';

// components
import AampeTooltip from '../../shared/AampeTooltip/AampeTooltip';

interface CompactTabItemUI {
    color?: string;
    activeBGColor?: string;
    activeTextColor?: string;
}
const shouldForwardProps = createShouldForwardProp([
    'isActive',
    'color',
    'activeTextColor',
    'activeBGColor',
]);

interface TabStyledProps {
    isActive: boolean;
    activeBGColor?: string;
    activeTextColor?: string;
    color?: string;
}

export interface CompactTabItem extends CompactTabItemUI {
    key: string;
    name: string;
    isBetaMode?: boolean;
    isPrimary?: boolean;
    Icon?: any;
    iconColor?: string;
    tooltip?: string;
}

export interface CompactTabProps {
    tabItem: CompactTabItem;
    onClick: () => void;
    isActive: boolean;
    tabStyle?: React.CSSProperties;
}

const CompactTab: FC<PropsWithChildren<CompactTabProps>> = ({
    tabItem: {
        name,
        color = aampeTheme.colors.textAccessibleWG01,
        activeBGColor = aampeTheme.colors.primary100,
        activeTextColor = aampeTheme.colors.solidA01,
        Icon,
        iconColor,
        tooltip,
    },
    isActive,
    onClick,
    tabStyle,
}) => (
    <TabButton
        role="tab"
        data-testid={`tab-${name}`}
        activeBGColor={activeBGColor}
        activeTextColor={activeTextColor}
        color={color}
        isActive={isActive}
        onClick={onClick}
        style={tabStyle}
    >
        {Icon && <Icon color={iconColor} />}
        {tooltip ? (
            <AampeTooltip title={tooltip} customZIndex={100}>
                <div>{name}</div>
            </AampeTooltip>
        ) : (
            name
        )}
    </TabButton>
);

const TabButton = styled('div').withConfig({
    shouldForwardProp: shouldForwardProps,
})<TabStyledProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: ${aampeTheme.spacing(1)};
    padding: ${aampeTheme.spacing(1, 2)};
    height: ${aampeTheme.size.inputHeight.defaultV2};
    cursor: pointer;
    background-color: ${(props: TabStyledProps) =>
        props.isActive
            ? aampeTheme.colors.white
            : aampeTheme.colors.interactiveWG01};
    border-top-right-radius: ${aampeTheme.borderRadius.default};
    border-top-left-radius: ${aampeTheme.borderRadius.default};
    font-size: ${aampeTheme.size.font.default};
    word-wrap: break-word;
    font-weight: ${(props: TabStyledProps) => (props.isActive ? 500 : 400)};
    color: ${(props: TabStyledProps) =>
        props.isActive
            ? props.activeTextColor ?? aampeTheme.colors.white
            : props.color};
    margin-right: ${aampeTheme.spacing4(2)};
`;

export default CompactTab;
